import { AtIcon, CircleCheckIcon, CircleCrossIcon, StackIcon } from "@xola/icons";
import { Button, Skeleton } from "@xola/ui-kit";
import { DateFormat, formatDate, PurchaseItemStatus } from "common";
import { Omit, sumBy } from "lodash";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Except } from "type-fest";
import {
    Experience,
    ExperiencePurchaseItem,
    PaymentReminderType,
    Permission,
    Purchase,
    PurchaseItemObject,
} from "types";
import { useResource } from "../../hooks/useResource";
import { findPaymentReminder, getCustomerName, isInvoicePurchase, isPurchaseRefundDue } from "../../models/Purchase";
import { IsGranted } from "../Common/IsGranted";
import { NoTranslate } from "../Common/NoTranslate";
import { SecondaryButton } from "../Common/SecondaryButton";
import { AcceptOrDeclinePurchaseModal } from "../Dashboard/AcceptOrDeclinePurchaseModal/AcceptOrDeclinePurchaseModal";
import { PendingPurchaseModal } from "../Dashboard/PendingPurchaseModal";
import { ErrorBoundary } from "../ErrorBoundary";
import { CollectBalanceButton } from "../PurchasesMenu/PaymentSummary/CollectBalance";
import { ReturnExcessPaymentButton } from "../PurchasesMenu/PaymentSummary/ReturnExcessPaymentButton";
import { NotificationPurchaseTile } from "./NotificationPurchaseTile";

interface NotificationItemProps {
    purchase: Purchase;
    onDismiss: (purchaseId: ID) => void;
    onClose?: () => void;
    onSuccess?: (purchaseId: string) => void;
}

export const CollectBalance = ({ purchase, onDismiss }: Omit<NotificationItemProps, "onClose" | "onSuccess">) => {
    let title = "Balance Due";
    let button = "Collect Balance";
    let due;

    const balanceDueTraveler = findPaymentReminder(purchase, [
        PaymentReminderType.BALANCE_DUE_TRAVELER,
        PaymentReminderType.SELLER_DEPOSIT,
    ]);
    const purchaseItem = purchase.items[0] as ExperiencePurchaseItem;
    if (balanceDueTraveler) {
        due = <NoTranslate>{formatDate(balanceDueTraveler?.dueAt, DateFormat.DATE)}</NoTranslate>;
    } else if (purchase.items.length === 1 && purchaseItem.arrivalDate) {
        due = (
            <span>
                (Arrival Date: <NoTranslate>{formatDate(purchaseItem.arrivalDate, DateFormat.DATE)}</NoTranslate>)
            </span>
        );
    } else {
        due = <span />;
    }

    if (isInvoicePurchase(purchase)) {
        title = "Invoice Due";
        button = "Collect Invoice Balance";
        const invoiceTraveler = findPaymentReminder(purchase, [
            PaymentReminderType.INVOICE_SELLER,
            PaymentReminderType.INVOICE_TRAVELER,
        ]);
        if (invoiceTraveler?.dueAt) {
            due = formatDate(invoiceTraveler?.dueAt, DateFormat.DATE);
        }
    }

    if (isPurchaseRefundDue(purchase)) {
        title = "Refund Due";
        button = "Refund Excess Payment";
    }

    return (
        <div className="pt-8" data-testid="notification-item">
            <div className="pb-2">
                <b className="pr-2">{title}</b>
                <var data-var="due">{due}</var>
            </div>
            <NotificationPurchaseTile purchase={purchase} paymentTitle={title} amount={purchase.balance} />
            <div className="flex justify-between">
                {isPurchaseRefundDue(purchase) ? (
                    <ErrorBoundary fallbackMessage="Error rendering refund modal">
                        <ReturnExcessPaymentButton
                            purchase={purchase}
                            item={purchase.items[0]}
                            icon={<StackIcon />}
                            size="small"
                        />
                    </ErrorBoundary>
                ) : (
                    <ErrorBoundary fallbackMessage="Error rendering collect balance modal">
                        <CollectBalanceButton purchase={purchase} title={button} icon={<StackIcon />} size="small" />
                    </ErrorBoundary>
                )}

                <IsGranted permissions={Permission.NOTIFICATIONS_DISMISS_VIEW}>
                    <SecondaryButton size="small" onClick={() => onDismiss(purchase.id)}>
                        Dismiss
                    </SecondaryButton>
                </IsGranted>
            </div>
        </div>
    );
};

export const AbandonPurchase = ({ purchase, onDismiss }: Except<NotificationItemProps, "onClose">) => {
    const abandonedItem = purchase.items?.find((item) => item.status === PurchaseItemStatus.ABANDONED);
    const item = isMistPurchase(purchase) ? abandonedItem : purchase.items?.[0];
    const { data: experience, isLoading } = useResource<Experience>(["products", item?.product?.id]);

    if (item?.object !== PurchaseItemObject.EXPERIENCE) {
        // Only experience based bookings can be considered for this component
        return null;
    }

    if (isLoading || !item) {
        return (
            <Skeleton height={160} classNames={{ container: "my-8 mr-3" }}>
                <div className="mr-3 h-14 w-14 rounded" />
            </Skeleton>
        );
    }

    const subject = `Your booking for ${experience?.name}`;

    const getEmailBody = () => {
        let body =
            `Customer name: ${getCustomerName(purchase)}` +
            "\n" +
            `Experience: ${experience?.name}` +
            "\n" +
            `Guests: ${sumBy(purchase.items, "quantity")}` +
            "\n" +
            `Arrival: ${item.arrivalDate}`;

        if (item.arrivalTime) {
            body += ` at ${formatDate(item.arrivalDateTime, DateFormat.DATE_TIME)}`;
        }

        return body;
    };

    const handleEmailGuest = () => {
        window.location.href = `mailto:${purchase.customerEmail}?subject=${subject}&body=${encodeURI(getEmailBody())}`;
    };

    return (
        <div className="pt-8">
            <div className="pb-2">
                <b className="pr-2">Abandoned Cart</b>
                <NoTranslate>{formatDate(item.arrivalDateTime, DateFormat.DATE)}</NoTranslate>
            </div>
            <NotificationPurchaseTile
                purchase={purchase}
                paymentTitle="Total Value"
                amount={purchase.amount}
                isClickable={false}
            />
            <div className="flex justify-between">
                <Link color="white" to="#" onClick={() => handleEmailGuest()}>
                    <Button type="primary" size="small" icon={<AtIcon />}>
                        Email Guest
                    </Button>
                </Link>
                <IsGranted permissions={Permission.NOTIFICATIONS_DISMISS_VIEW}>
                    <SecondaryButton size="small" onClick={() => onDismiss(purchase.id)}>
                        Dismiss
                    </SecondaryButton>
                </IsGranted>
            </div>
        </div>
    );
};

export const AcceptOrDeclinePurchase = ({ purchase, onDismiss, onClose, onSuccess }: NotificationItemProps) => {
    const [activePurchase, setActivePurchase] = useState<Purchase>();
    const [pendingModal, setPendingModal] = useState(false);
    const [updatedStatus, setUpdatedStatus] = useState<
        PurchaseItemStatus.CONFIRMED | PurchaseItemStatus.DECLINED | null
    >(null);

    const handleModalClose = () => {
        setActivePurchase(undefined);
        setPendingModal(false);
    };

    const handleAcceptOrDeclineModalClose = () => {
        setActivePurchase(undefined);
        setUpdatedStatus(null);
        onClose?.();
    };

    const handleAcceptOrDeclineModalOpen = (status: PurchaseItemStatus.CONFIRMED | PurchaseItemStatus.DECLINED) => {
        setActivePurchase(purchase);
        setUpdatedStatus(status);
    };

    return (
        <>
            {purchase.items.map((item) => {
                if (item.object !== PurchaseItemObject.EXPERIENCE) {
                    return null;
                }

                return (
                    <div key={item.id} className="pt-8" data-testid="notification-item">
                        <div className="pb-2">
                            <b className="pr-2">Pending Confirmation</b>
                            <NoTranslate>{formatDate(item.arrivalDateTime, DateFormat.DATE)}</NoTranslate>
                        </div>
                        <NotificationPurchaseTile
                            purchase={purchase}
                            paymentTitle="Total Value"
                            amount={purchase.amount}
                        />
                        <div className="flex justify-between">
                            <div className="flex items-center space-x-4">
                                <Button
                                    color="danger"
                                    className="items-center"
                                    size="small"
                                    icon={<CircleCrossIcon size="medium" />}
                                    onClick={() => handleAcceptOrDeclineModalOpen(PurchaseItemStatus.DECLINED)}
                                >
                                    Decline
                                </Button>
                                <Button
                                    size="small"
                                    className="items-center"
                                    color="success"
                                    icon={<CircleCheckIcon size="medium" />}
                                    onClick={() => handleAcceptOrDeclineModalOpen(PurchaseItemStatus.CONFIRMED)}
                                >
                                    Confirm
                                </Button>
                                {activePurchase && (
                                    <>
                                        <PendingPurchaseModal
                                            isOpen={pendingModal}
                                            item={item}
                                            purchase={activePurchase}
                                            onClose={handleModalClose}
                                            onSuccess={onSuccess}
                                        />
                                        {updatedStatus !== null && (
                                            <AcceptOrDeclinePurchaseModal
                                                isOpen={!!updatedStatus}
                                                purchase={activePurchase}
                                                item={item}
                                                updatedStatus={updatedStatus}
                                                onClose={handleAcceptOrDeclineModalClose}
                                                onSuccess={onSuccess}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                            <IsGranted permissions={Permission.NOTIFICATIONS_DISMISS_VIEW}>
                                <SecondaryButton size="small" onClick={() => onDismiss(purchase.id)}>
                                    Dismiss
                                </SecondaryButton>
                            </IsGranted>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

const isMistPurchase = (purchase: Purchase) => {
    return purchase.items?.length > 1;
};
